import { motion, useReducedMotion } from 'framer-motion'
import { AfosLogo } from './Logo'

type Orbit = { r: number; duration: number; dots: { angle: number; size: number; color: string }[]; dash?: string; reverse?: boolean }

const ORBITS: Orbit[] = [
  { r: 62, duration: 14, dash: '2 7', dots: [{ angle: 20, size: 3.5, color: '#2EE6C5' }, { angle: 200, size: 2.5, color: '#E8F1FF' }] },
  { r: 84, duration: 22, reverse: true, dots: [{ angle: 110, size: 4.5, color: '#FFB347' }, { angle: 300, size: 2, color: '#9DB0D4' }] },
  { r: 106, duration: 34, dash: '1 10', dots: [{ angle: 60, size: 3, color: '#FF6B8A' }, { angle: 170, size: 2, color: '#2EE6C5' }, { angle: 260, size: 2.5, color: '#E8F1FF' }] },
]

const SIZE = 240
const C = SIZE / 2

/** The AFOS 360 mark with a glowing core and particles on slow, counter-rotating orbits. */
export function LogoOrbit() {
  const reduce = useReducedMotion()

  return (
    <div className="relative mx-auto h-[200px] w-[200px] sm:h-[240px] sm:w-[240px]" aria-hidden>
      <motion.div
        className="absolute inset-[22%] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(46,230,197,0.45), rgba(46,230,197,0) 70%)' }}
        animate={reduce ? undefined : { scale: [1, 1.18, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 h-full w-full overflow-visible">
        <defs>
          <linearGradient id="afosOrbitStroke" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#2EE6C5" stopOpacity="0.7" />
            <stop offset="0.5" stopColor="#9DB0D4" stopOpacity="0.25" />
            <stop offset="1" stopColor="#FFB347" stopOpacity="0.7" />
          </linearGradient>
          <filter id="afosDotGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {ORBITS.map((orbit, i) => (
          <motion.g
            key={orbit.r}
            style={{ transformOrigin: `${C}px ${C}px` }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={
              reduce
                ? { opacity: 1, scale: 1 }
                : { opacity: 1, scale: 1, rotate: orbit.reverse ? -360 : 360 }
            }
            transition={{
              opacity: { delay: 0.25 + i * 0.15, duration: 0.8 },
              scale: { delay: 0.25 + i * 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
              rotate: { duration: orbit.duration, repeat: Infinity, ease: 'linear' },
            }}
          >
            <circle
              cx={C}
              cy={C}
              r={orbit.r}
              fill="none"
              stroke="url(#afosOrbitStroke)"
              strokeWidth={1}
              strokeDasharray={orbit.dash}
            />
            {orbit.dots.map((dot) => {
              const rad = (dot.angle * Math.PI) / 180
              return (
                <circle
                  key={dot.angle}
                  cx={C + Math.cos(rad) * orbit.r}
                  cy={C + Math.sin(rad) * orbit.r}
                  r={dot.size}
                  fill={dot.color}
                  filter="url(#afosDotGlow)"
                />
              )
            })}
          </motion.g>
        ))}
      </svg>
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.7, rotate: -20 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <AfosLogo markOnly size={88} className="drop-shadow-[0_0_24px_rgba(46,230,197,0.45)]" />
      </motion.div>
    </div>
  )
}
